import React, { useState } from 'react';
import toast from 'react-hot-toast';
import DownloadIcon from '@mui/icons-material/Download';
import { fetchDownloadUrl, financeError } from '../../hooks/useFinance';
import {
  DOCUMENT_KINDS,
  FILE_KINDS,
  STATUS_LABELS,
  STATUS_STYLES,
  documentKindLabel,
  formatDateTime,
  formatSize,
} from './financeText';

// Opens a file through a 5-minute link fetched at click time. A blank tab
// is opened first, within the click, so popup blockers allow it; the link
// is never kept anywhere.
function DownloadButton({ scope, invoiceId, file }) {
  const [busy, setBusy] = useState(false);
  const open = async () => {
    const tab = window.open('', '_blank');
    if (tab) tab.opener = null;
    setBusy(true);
    try {
      const url = await fetchDownloadUrl(scope, invoiceId, file.id);
      if (!url) throw new Error();
      if (tab) tab.location.href = url;
      else window.open(url, '_blank', 'noopener');
    } catch (error) {
      if (tab) tab.close();
      toast.error(financeError(error, "We couldn't open the file."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={open}
      disabled={busy}
      aria-label={`Open ${file.original_name}`}
      className="inline-flex items-center gap-1 text-xs text-claret-500 hover:underline disabled:opacity-50"
    >
      <DownloadIcon sx={{ fontSize: 14 }} />
      {busy ? 'Opening…' : 'Open'}
    </button>
  );
}

function FileRow({ scope, invoiceId, file, muted }) {
  return (
    <li className="flex items-center justify-between gap-3 py-1.5">
      <div className="min-w-0">
        <p
          className={`truncate text-sm ${
            muted ? 'text-gray-500' : 'text-gray-900'
          }`}
        >
          {file.label && (
            <span className="mr-1 font-medium">{file.label}:</span>
          )}
          {file.original_name}
          {file.version > 1 && (
            <span className="ml-1 text-xs text-gray-400">v{file.version}</span>
          )}
        </p>
        <p className="text-[11px] text-gray-500">
          {formatSize(file.size)} · uploaded {formatDateTime(file.uploaded_at)}
          {file.uploaded_by && ` by ${file.uploaded_by}`}
          {file.superseded_at &&
            ` · replaced ${formatDateTime(file.superseded_at)}`}
        </p>
      </div>
      <DownloadButton scope={scope} invoiceId={invoiceId} file={file} />
    </li>
  );
}

function KindGroup({ scope, invoiceId, label, files, sideBySide }) {
  // Versioned kinds show the current file first, then earlier versions;
  // `other` documents are separate files, all shown.
  const current = sideBySide
    ? files.filter((f) => !f.superseded_at)
    : files.filter((f) => !f.superseded_at).slice(0, 1);
  const earlier = files.filter((f) => !current.includes(f));
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        {label}
      </p>
      {files.length === 0 && <p className="text-sm text-gray-500">No file.</p>}
      <ul className="m-0 list-none divide-y divide-gray-100 p-0">
        {current.map((f) => (
          <FileRow key={f.id} scope={scope} invoiceId={invoiceId} file={f} />
        ))}
        {earlier.map((f) => (
          <FileRow
            key={f.id}
            scope={scope}
            invoiceId={invoiceId}
            file={f}
            muted
          />
        ))}
      </ul>
    </div>
  );
}

// The consultant's invoice files, then documents finance has attached
// (payslip, proof of payment, other). Unknown kinds show by their name.
export default function InvoiceFiles({ scope, invoice, documentsEmptyText }) {
  const files = invoice.files ?? [];
  const invoiceKinds = FILE_KINDS.map((k) => k.kind);
  const documents = files.filter((f) => !invoiceKinds.includes(f.kind));
  const documentKinds = [
    ...DOCUMENT_KINDS.map((k) => k.kind),
    ...documents.map((f) => f.kind),
  ].filter((kind, i, all) => all.indexOf(kind) === i);
  const shownDocumentKinds = documentKinds.filter((kind) =>
    documents.some((f) => f.kind === kind)
  );

  return (
    <div className="space-y-3">
      {FILE_KINDS.map(({ kind, label }) => (
        <KindGroup
          key={kind}
          scope={scope}
          invoiceId={invoice.id}
          label={label}
          files={files.filter((f) => f.kind === kind)}
        />
      ))}
      {(shownDocumentKinds.length > 0 || documentsEmptyText) && (
        <div className="space-y-3 border-t border-gray-100 pt-3">
          <p className="text-sm font-semibold text-gray-900">
            Finance documents
          </p>
          {shownDocumentKinds.length === 0 && (
            <p className="text-sm text-gray-500">{documentsEmptyText}</p>
          )}
          {shownDocumentKinds.map((kind) => (
            <KindGroup
              key={kind}
              scope={scope}
              invoiceId={invoice.id}
              label={documentKindLabel(kind)}
              files={documents.filter((f) => f.kind === kind)}
              sideBySide={kind === 'other'}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function StatusBadge({ status }) {
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
        STATUS_STYLES[status] || 'bg-gray-100 text-gray-600'
      }`}
    >
      {STATUS_LABELS[status] || status}
    </span>
  );
}
